import type { Theme, WithStyles } from '@material-ui/core'
import { createStyles, Divider, Grid, withStyles } from '@material-ui/core'
import type { WithDialogStackContext, DropdownSelectOption } from '@paintscout/ui'
import {
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  InputField,
  ManufacturerColorPicker,
  OverridableInputField,
  Typography,
  withDialogStackContext
} from '@paintscout/ui'

import type { Product, EditableRate, UpdateableProduct } from '@paintscout/util/builder'
import { getFeatures, getObjectLabels, uuid } from '@paintscout/util/builder'
import get from 'lodash/get'
import type { OptionsDocument, OverridableValue, QuoteDocument } from 'paintscout'
import React, { useState } from 'react'
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle'
import classnames from 'classnames'
import { CalculationProductHelper } from '../../EditAreaDialog/CalculationHelper'
import ProductSelectDialog from '../../ProductSelectDialog'
import { useQuote } from '../../../context'

export interface AreaSubstrateProductFormProps extends WithStyles<typeof styles>, WithDialogStackContext {
  options: OptionsDocument
  products: UpdateableProduct[]
  quoteType?: string
  rate?: EditableRate
  quote?: QuoteDocument
  disabled?: boolean
  areaKey?: string
  areaSubstrateKey?: string
  measurementLabel: string
  onChange?: (index: number, product: UpdateableProduct, custom?: boolean) => void
  onRemoveProduct?: (index: number) => void
  checkParentHasChanges?: () => boolean
}

export const styles = (theme: Theme) => {
  return createStyles({
    root: {},
    selectButton: {
      marginBottom: theme.typography.pxToRem(10),
      marginTop: theme.typography.pxToRem(30)
    },
    showToggle: {
      display: 'grid',
      alignItems: 'center',
      gridGap: theme.spacing(),
      whiteSpace: 'nowrap',
      gridTemplateColumns: 'min-content auto'
    },
    productButton: {
      padding: 0,
      height: 'auto'
    },
    showFormula: {
      marginBottom: theme.spacing(0.5)
    },
    formulaControlContainer: {
      [theme.breakpoints.down('md')]: {
        marginTop: theme.spacing(3)
      },
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(0)
      }
    },
    formRow: {
      paddingBottom: theme.spacing()
    },
    productContainer: {
      display: 'flex',
      flexDirection: 'row',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column'
      }
    },
    leftProductSection: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column'
      }
    },
    inputWidth: {
      [theme.breakpoints.down('sm')]: {
        width: '100%'
      }
    },
    rightProductSection: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginLeft: theme.spacing(5),
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginLeft: theme.spacing(0),
        marginTop: theme.spacing(2)
      }
    },
    rightProductSectionContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderLeft: '1px solid #e0e0e0',
      paddingTop: theme.spacing(0),
      paddingBottom: theme.spacing(0),
      [theme.breakpoints.down('sm')]: {
        borderTop: '1px solid #e0e0e0',
        borderLeft: 'none',
        flexDirection: 'row',
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingBottom: theme.spacing(0),
        paddingTop: theme.spacing(2)
      }
    },
    rightSectionItem: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    },
    removeIcon: {
      cursor: 'pointer',
      padding: theme.spacing(),
      fontWeight: '500',

      '&:hover': {
        color: theme.palette.error.main,
        transition: 'color 0.3s ease',
        // fontWeight: '600',
        '> svg': {
          // Targeting <path> within the class
          fontSize: '3rem'
        }
      }
    },
    greyStyle: {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
      padding: theme.spacing(1),
      marginLeft: theme.spacing(),
      marginTop: theme.spacing(),
      borderRadius: theme.borderRadius.md
    },
    divider: {
      padding: '.2px',
      marginBottom: theme.spacing(),
      width: '95%',
      marginLeft: '2.5%'
    },
    inputLabelWrapper: {
      '& label': {
        whiteSpace: 'wrap',
        maxWidth: 'calc(100% + 60px)',
        minWidth: 'calc(100% + 60px)'
      }
    }
  })
}

function AreaSubstrateProductForm(props: AreaSubstrateProductFormProps) {
  const {
    classes,
    products,
    rate,
    options,
    quoteType,
    dialogStackContext,
    quote,
    areaKey,
    disabled,
    areaSubstrateKey,
    measurementLabel
  } = props
  // We want to check correct dimensions based on rateType to determine if we should show the product fields
  const { rateType } = rate
  const { isNoteEditable } = useQuote()
  const features = getFeatures({ options })
  const { currency, unit } = getObjectLabels({ options })
  const materialCostFeature = get(features, 'quotes.materialCost.enabled', false)
  const multiProductRatesFeature = get(features, 'quotes.areaSubstrates.multiProductRates', false)

  const [showFormulaIndex, setShowFormulaIndex] = useState({})
  const [attemptedSubmit, setAttemptedSubmit] = useState<boolean>(false)
  const [isEditableIndex, setIsEditableIndex] = useState({})

  function handleShowFormulaChange(event: React.ChangeEvent<HTMLInputElement>, id: string) {
    const checked = event.target.checked
    setShowFormulaIndex((showFormulaIndex) => ({
      ...showFormulaIndex,
      [id]: checked
    }))
  }
  function handleIsEditableChange(isEditable: boolean, id: string) {
    setIsEditableIndex((isEditableIndex) => ({
      ...isEditableIndex,
      [id]: !isEditable
    }))
  }

  return (
    <Grid container={true} spacing={3} alignItems="flex-start" classes={{ container: classes.formRow }}>
      {products.map((product, index) => {
        const unitLabel = product?.unitLabel ?? 'gal'
        const productKey = product.key.toString()
        const uniqueProductKey = productKey + '-' + index
        const showProductFields = product
        const isEditable = multiProductRatesFeature ? isEditableIndex[uniqueProductKey] : true
        const showFormula = showFormulaIndex[uniqueProductKey]
        const isManufacturerProduct = !!product.manufacturer

        if (!showProductFields) return null
        return (
          <>
            <Grid key={index} item xs={12} className={classes.productContainer}>
              <Grid container spacing={2} className={classes.leftProductSection} xs={12} md={10}>
                {isEditable ? (
                  <>
                    <Grid className={`${classes.inputWidth}`} item xs={12} md={6}>
                      <InputField
                        showErrorBeforeValidation={!product.label.length && attemptedSubmit}
                        label={'Name'}
                        placeholder={'Product Label'}
                        fullWidth={true}
                        name={'label'}
                        disabled={disabled || isManufacturerProduct}
                        value={product ? product.label : ''}
                        onChange={handleProductLabelChange(index)}
                        required
                        error={!product.label.length}
                      />
                    </Grid>
                    <Grid className={classes.inputWidth} item sm={12} md={6}>
                      {isManufacturerProduct ? (
                        <ManufacturerColorPicker
                          manufacturer={product.manufacturer}
                          value={{ value: product.color, label: product.color }}
                          onChange={(option) => handleManufacturerProductChange(index, option)}
                          onCreateOption={(inputValue) => {
                            handleManufacturerProductChange(index, { label: inputValue, value: inputValue })
                          }}
                        />
                      ) : (
                        <InputField
                          fullWidth={true}
                          label={'Color'}
                          name={'color'}
                          placeholder={'Color'}
                          disabled={disabled && !isNoteEditable}
                          value={product && product.color ? product.color : ''}
                          readOnly={!product}
                          onChange={handleProductLabelChange(index)}
                        />
                      )}
                    </Grid>
                    {rateType !== 'lnft' && rateType !== 'quantity' && (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        classes={{ root: `${'coverage-field'} ${classes.inputWidth}` }}
                      >
                        <OverridableInputField
                          classes={{ root: classes.inputLabelWrapper }}
                          fullWidth={true}
                          label={`Coverage (${unit.abbreviation.square}/${unitLabel})`}
                          format={'rate'}
                          autoSelect={true}
                          hideHelp={false}
                          placeholder={'Product Coverage'}
                          disabled={disabled}
                          value={product.coverage}
                          readOnly={!product}
                          resetBlank={true}
                          onChange={handleProductValueChange(index, 'coverage')}
                        />
                      </Grid>
                    )}
                    {rateType === 'lnft' && (
                      <Grid
                        item={true}
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        classes={{ root: `${'coverage-field'} ${classes.inputWidth}` }}
                      >
                        <OverridableInputField
                          classes={{ root: classes.inputLabelWrapper }}
                          fullWidth={true}
                          label={`Coverage (${unit.abbreviation.linear}/${unitLabel})`}
                          format={'rate'}
                          autoSelect={true}
                          hideHelp={false}
                          disabled={disabled}
                          placeholder={'Product Coverage'}
                          value={product.lnftCoverage}
                          readOnly={!product}
                          resetBlank={true}
                          onChange={handleProductValueChange(index, 'lnftCoverage')}
                        />
                      </Grid>
                    )}
                    {rateType === 'quantity' && (
                      <Grid
                        item={true}
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        classes={{ root: `${'coverage-field'} ${classes.inputWidth}` }}
                      >
                        <OverridableInputField
                          classes={{ root: classes.inputLabelWrapper }}
                          fullWidth={true}
                          label={`Coverage (${unitLabel}/item)`}
                          format={'rate'}
                          autoSelect={true}
                          hideHelp={false}
                          placeholder={'Product Coverage'}
                          disabled={disabled}
                          value={product.quantityCoverage}
                          readOnly={!product}
                          resetBlank={true}
                          onChange={handleProductValueChange(index, 'quantityCoverage')}
                        />
                      </Grid>
                    )}
                    <Grid
                      item={true}
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      classes={{ root: `${'volume-field'} ${classes.inputWidth}` }}
                    >
                      <OverridableInputField
                        classes={{ root: classes.inputLabelWrapper }}
                        fullWidth={true}
                        label={`Volume (${unitLabel})`}
                        format={'rate'}
                        autoSelect={true}
                        placeholder={'Product Volume'}
                        disabled={disabled}
                        value={product.volume}
                        readOnly={!product}
                        resetBlank={true}
                        helpText={''}
                        onChange={handleProductValueChange(index, 'volume')}
                      />
                    </Grid>
                    {materialCostFeature && (
                      <>
                        <Grid
                          item={true}
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          classes={{ root: `${'unit-price-field'} ${classes.inputWidth}` }}
                        >
                          <OverridableInputField
                            classes={{ root: classes.inputLabelWrapper }}
                            fullWidth={true}
                            label={`Unit Price (${currency.symbol}/${unitLabel})`}
                            format={'price'}
                            autoSelect={true}
                            placeholder={'Unit Price'}
                            disabled={disabled}
                            value={product.price}
                            readOnly={!product}
                            resetBlank={true}
                            helpText={''}
                            onChange={handleProductValueChange(index, 'price')}
                          />
                        </Grid>
                        <Grid
                          item={true}
                          xs={12}
                          sm={6}
                          md={6}
                          lg={3}
                          classes={{ root: `${'total-price-field'} ${classes.inputWidth}` }}
                        >
                          <OverridableInputField
                            classes={{ root: classes.inputLabelWrapper }}
                            fullWidth={true}
                            label={`Total Price (${currency.symbol})`}
                            format={'price'}
                            autoSelect={true}
                            placeholder={'Total Price'}
                            value={product.totalPrice}
                            disabled={disabled}
                            readOnly={!product}
                            resetBlank={true}
                            helpText={''}
                            flipX={true}
                            onChange={handleProductValueChange(index, 'totalPrice')}
                          />
                        </Grid>
                        <Grid container sm={12} md={6} lg={12} item justifyContent="space-between">
                          <FormControlLabel
                            classes={{
                              root: classnames({ [classes.formulaControlContainer]: true, [classes.showFormula]: true })
                            }}
                            label={'Show Formula'}
                            control={
                              <Checkbox
                                onChange={(e) => handleShowFormulaChange(e, uniqueProductKey)}
                                checked={showFormula}
                              />
                            }
                          />
                        </Grid>

                        {showFormula && (
                          <Grid item={true} xs={12}>
                            <Collapse show={showFormula}>
                              <CalculationProductHelper
                                quote={quote}
                                options={options}
                                areaKey={areaKey}
                                areaSubstrateKey={areaSubstrateKey}
                                unitLabel={unitLabel}
                                measurementLabel={measurementLabel}
                                rateType={rateType}
                                productIndex={index}
                              />
                            </Collapse>
                          </Grid>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Grid className={`${classes.inputWidth}`} item xs={12} md={6}>
                      <Typography variant={'overline'}>Name</Typography>
                      <Typography variant={'body1'}>{product.label}</Typography>
                    </Grid>
                    <Grid className={classes.inputWidth} item sm={12} md={6}>
                      <Typography variant={'overline'}>Color</Typography>
                      <Typography variant={'body1'}>{product?.color || '- None -'}</Typography>
                    </Grid>
                    <Grid container spacing={1} xs={12} classes={{ root: `${classes.greyStyle}` }}>
                      {rateType !== 'lnft' && rateType !== 'quantity' && (
                        <Grid item={true} xs={12} sm={6} md={3} classes={{ root: 'coverage-field' }}>
                          <Typography
                            variant={'overline'}
                          >{`Coverage (${unit.abbreviation.square}/${unitLabel})`}</Typography>
                          <Typography
                            variant={'body1'}
                          >{`${product.coverage.useCustom ? product.coverage.custom : product.coverage.default}`}</Typography>
                        </Grid>
                      )}
                      {rateType === 'lnft' && (
                        <Grid item={true} xs={12} sm={6} md={3} classes={{ root: 'coverage-field' }}>
                          <Typography
                            variant={'overline'}
                          >{`Coverage (${unit.abbreviation.linear}/${unitLabel})`}</Typography>
                          <Typography
                            variant={'body1'}
                          >{`${product.lnftCoverage.useCustom ? product.lnftCoverage.custom : product.lnftCoverage.default}`}</Typography>
                        </Grid>
                      )}
                      {rateType === 'quantity' && (
                        <Grid item={true} xs={12} sm={6} md={3} classes={{ root: 'coverage-field' }}>
                          <Typography variant={'overline'}>{`Coverage (${unitLabel}/item)`}</Typography>
                          <Typography
                            variant={'body1'}
                          >{`${product.quantityCoverage.useCustom ? product.quantityCoverage.custom : product.quantityCoverage.default}`}</Typography>
                        </Grid>
                      )}
                      <Grid item={true} xs={12} sm={6} md={2} classes={{ root: `volume-field` }}>
                        <Typography variant={'overline'}>{`Volume (${unitLabel})`}</Typography>
                        <Typography
                          variant={'body1'}
                        >{`${Math.round((product.volume.useCustom ? product.volume.custom : product.volume.default) * 100) / 100}`}</Typography>
                      </Grid>
                      {materialCostFeature && (
                        <>
                          <Grid item={true} xs={12} sm={6} md={3} classes={{ root: 'unit-price-field' }}>
                            <Typography
                              variant={'overline'}
                            >{`Unit Price (${currency.symbol}/${unitLabel})`}</Typography>
                            <Typography
                              variant={'body1'}
                            >{`${product.price.useCustom ? product.price.custom : product.price.default}`}</Typography>
                          </Grid>
                          <Grid item={true} xs={12} sm={6} md={3} classes={{ root: 'total-price-field' }}>
                            <Typography variant={'overline'}>{`Total Price (${currency.symbol})`}</Typography>
                            <Typography
                              variant={'body1'}
                            >{`${(product.totalPrice.useCustom ? product.totalPrice.custom : product.totalPrice.default).toFixed(2)}`}</Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                    {materialCostFeature && (
                      <>
                        <Grid container item xs={12} justifyContent="space-between">
                          <FormControlLabel
                            classes={{ root: classes.showFormula }}
                            label={'Show Formula'}
                            control={
                              <Checkbox
                                onChange={(e) => handleShowFormulaChange(e, uniqueProductKey)}
                                checked={showFormula}
                              />
                            }
                          />
                        </Grid>

                        {showFormula && (
                          <Grid item={true} xs={12}>
                            <Collapse show={showFormula}>
                              <CalculationProductHelper
                                quote={quote}
                                options={options}
                                areaKey={areaKey}
                                areaSubstrateKey={areaSubstrateKey}
                                unitLabel={unitLabel}
                                measurementLabel={measurementLabel}
                                rateType={rateType}
                                productIndex={index}
                              />
                            </Collapse>
                          </Grid>
                        )}
                      </>
                    )}
                  </>
                )}
              </Grid>
              <Grid className={classes.rightProductSection} item sm={12} md={2}>
                <Grid className={classes.rightProductSectionContainer} container spacing={1} xs={12}>
                  {!disabled && (
                    <Grid className={classes.rightSectionItem} item sm={5} md={12}>
                      <div className={classes.removeIcon} onClick={() => handleRemoveProduct(index)}>
                        <RemoveCircleIcon />
                      </div>
                    </Grid>
                  )}
                  <Grid item xs={2} md={4}></Grid>
                  {!disabled && multiProductRatesFeature && (
                    <Grid className={classes.rightSectionItem} item sm={5} md={12}>
                      <Button
                        variant={'text'}
                        size={'small'}
                        onClick={() => {
                          if (!product.label.length) {
                            setAttemptedSubmit(true)
                          } else {
                            setAttemptedSubmit(false)
                            handleIsEditableChange(isEditable, uniqueProductKey)
                          }
                        }}
                      >
                        {isEditable ? 'Done' : 'Edit'}
                      </Button>
                    </Grid>
                  )}
                  {!disabled && !multiProductRatesFeature && (
                    <Grid className={classes.rightSectionItem} item sm={5} md={12}>
                      <Button variant={'text'} size={'small'} onClick={() => handleProductSelect(index)}>
                        Change
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            {index !== products.length - 1 && <Divider classes={{ root: classes.divider }} />}
          </>
        )
      })}
      {((!multiProductRatesFeature && products.length === 0) || multiProductRatesFeature) && (
        <Grid item={true} xs={12} onClick={() => (disabled ? null : handleProductSelect())}>
          <Button
            fullWidth
            variant={'outlined'}
            size={'medium'}
            onClick={disabled ? null : () => handleProductSelect()}
            disabled={disabled}
          >
            + Add Product
          </Button>
        </Grid>
      )}
    </Grid>
  )

  function handleProductSelect(index?: number) {
    dialogStackContext.openDialog(ProductSelectDialog, {
      options,
      quoteTypes: quoteType ? [quoteType] : undefined,
      allowCustom: true,
      checkParentHasChanges: props.checkParentHasChanges,
      onConfirm: (product: Product, custom?: boolean) => {
        dialogStackContext.dismissDialog()

        let coverage
        let lnftCoverage
        let quantityCoverage
        if ((product && product?.coverage) || product?.lnftCoverage || product?.quantityCoverage) {
          coverage = product.coverage
          lnftCoverage = product.lnftCoverage
          quantityCoverage = product.quantityCoverage
        } else if (rate.coverage) {
          coverage = lnftCoverage = quantityCoverage = rate.coverage
        } else {
          coverage = lnftCoverage = quantityCoverage = 0
        }

        const newProduct = custom
          ? {
              key: uuid(),
              label: '',
              color: '',
              calculate: false,
              unitLabel: 'gal',
              coverage: { default: 0, useCustom: false, custom: 0 },
              lnftCoverage: { default: 0, useCustom: false, custom: 0 },
              quantityCoverage: { default: 0, useCustom: false, custom: 0 },
              volume: { default: 0, useCustom: false, custom: 0 },
              price: { default: 0, useCustom: false, custom: 0 },
              totalPrice: { default: 0, useCustom: false, custom: 0 },
              custom
            }
          : {
              ...product,
              color: product.color || '',
              calculate: !!product.calculate,
              coverage: { default: coverage, useCustom: false, custom: 0 },
              lnftCoverage: { default: lnftCoverage, useCustom: false, custom: 0 },
              quantityCoverage: { default: quantityCoverage, useCustom: false, custom: 0 },
              volume: { default: 0, useCustom: false, custom: 0 },
              price: { default: product.price, useCustom: false, custom: 0 },
              totalPrice: { default: 0, useCustom: false, custom: 0 }
            }

        if (props.onChange) {
          props.onChange(index ?? -1, newProduct, custom)
        }
      },
      onCancel: dialogStackContext.dismissDialog
    })
  }

  function handleProductLabelChange(index: number) {
    return function (ev: React.ChangeEvent<HTMLInputElement>) {
      const { name, value } = ev.target
      if (props.onChange) {
        const newProductValue = props.products[index].custom
          ? { ...props.products[index], [name]: value }
          : { ...props.products[index], [name]: value, key: uuid(), custom: true } // this is causing the featureProducts to not be found
        handleIsEditableChange(false, `${newProductValue.key}-${index}`)
        props.onChange(index, newProductValue)
      }
    }
  }

  function handleProductValueChange(index: number, name: string) {
    return function (value: OverridableValue) {
      if (props.onChange) {
        props.onChange(index, { ...props.products[index], [name]: value })
      }
    }
  }

  function handleManufacturerProductChange(index: number, option: DropdownSelectOption) {
    if (props.onChange) {
      props.onChange(index, { ...props.products[index], color: option.label })
    }
  }

  function handleRemoveProduct(index: number) {
    props.onRemoveProduct(index)
  }
}

export default withStyles(styles)(withDialogStackContext(AreaSubstrateProductForm))
